import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { useFlaggingCallback } from '@databases/flags';
import useClearEntriesKeyboardShortcut from '@databases/keyboard-shortcuts/useClearEntriesKeyboardShortcut';
import { ScrollPosition } from '@databases/layout/scroll-position';
import Z_INDEX from '@databases/layout/z-index';
import QuickCalculationsRow from '@databases/quick-calc/QuickCalculationsRow';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseEntry } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import useView from '@databases/views/useView';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import {
  TABLE_CLASSNAMES,
  TABLE_VIEW_HARD_COLUMN_MIN_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
  HALF_ROW_SELECT_BUTTON_WIDTH,
  NUMBERED_COLUMN_WIDTH,
} from '../../../../constants';
import { BorderContainer, InlineEditWrapperContainer } from '../../InlineEditWrapper';
import { useScope } from '../../Scope';
import TableResizer from '../../TableResizer';
import {
  FieldNameInlineContainer,
  FieldNameWithActionsContainer,
} from '../../database-field/field-name/FieldNameWithActions';
import {
  newTableBorderWidth,
  subtleBorder,
  SubtleTable,
  tableBorderRadius,
  tableRowMinHeight,
} from '../../styled';
import { selectButtonActiveStyle } from '../contextual-menu/styles';
import AddEntryButton from '../table-layout/AddEntryButton';

import { AddFieldButtonContainer } from './AddFieldButton';
import { StyledDot } from './AddFieldDot';
import TableLayoutHeaderRow from './TableLayoutHeaderRow';
import TableLayoutRows from './TableLayoutRows';
import useHasNumberedEntries from './UseHasNumberedEntries';
import { getGridTemplateColumns } from './table-columns';

const ACTIONS_MID_WIDTH = 1;
const INSERT_INDICATOR_HEIGHT_OFFSET = 10;

type InsertColumnIndicatorProps = {
  containerHeight: number;
};

const InsertColumnIndicator = css<InsertColumnIndicatorProps>`
  &.${TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE}, &.${TABLE_CLASSNAMES.INSERT_COLUMN_AFTER} {
    z-index: 1;

    &:before {
      content: ' ';
      position: absolute;
      top: 0;
      height: ${({ containerHeight }) =>
        `${containerHeight - INSERT_INDICATOR_HEIGHT_OFFSET}px`};
      width: 2px;
      background-color: ${colors.B200};
      pointer-events: none;
    }

    &.${TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE} {
      &:before {
        left: -1px;
      }
    }

    &.${TABLE_CLASSNAMES.INSERT_COLUMN_AFTER} {
      &:before {
        right: -1px;
      }
    }
  }
`;

export interface TableProps extends InsertColumnIndicatorProps {
  minWidth: number;
  gridTemplateColumns: string;
  actionsMidWidth: number;
  canEditContent?: boolean;
  hasNumberedEntries?: boolean;
}

export const Table = styled(SubtleTable)<TableProps>`
  overflow-wrap: anywhere;
  width: 100%;
  box-sizing: border-box;
  min-width: ${(props) => `${props.minWidth}px`};
  background: ${token('elevation.surface')};

  .${TABLE_CLASSNAMES.SELECT_BUTTON} {
    display: none;
    transition: opacity 0.2s;
    background-color: ${token('elevation.surface.sunken')};
    color: ${colors.N200};
    border-radius: 6px;

    &:hover {
      ${selectButtonActiveStyle};
      cursor: pointer;
    }

    .${TABLE_CLASSNAMES.DRAG_HANDLER_ICON} {
      display: flex;
    }

    .${TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON} {
      display: none;
    }

    &.${TABLE_CLASSNAMES.SHOW_AS_DRAGGING} {
      display: flex;
      ${selectButtonActiveStyle};
      cursor: grabbing;

      .${TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON} {
        display: flex;
      }

      .${TABLE_CLASSNAMES.DRAG_HANDLER_ICON} {
        display: none;
      }
    }
  }

  &,
  > * {
    display: flex;
    flex-direction: column;
  }

  > * > tr {
    display: grid;
    grid-template-columns: ${(props) => props.gridTemplateColumns};
  }

  > * > tr > * {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    box-sizing: border-box;
    min-width: 0;
    > ${FieldNameWithActionsContainer}, > ${InlineEditWrapperContainer} {
      flex-grow: 1;
    }
    a,
    input,
    textarea {
      font-weight: inherit;
    }
  }

  > * > tr > .entry-number-table-header,
  > * > tr > .entry-number-table-data {
    padding-top: 12px;
    position: sticky;
  }

  > thead > tr > th {
    ${InsertColumnIndicator};
  }

  > thead > tr > th:nth-last-child(2) {
    ${FieldNameWithActionsContainer} {
      margin-right: 0;
    }

    border-right: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
    border-start-end-radius: ${tableBorderRadius};
  }

  // If numbered entries is enabled, the 3rd child refers to the first column in the database.
  // Since numbered entries has a right border, we do not need a left border on the first column.
  > thead > tr > :nth-child(3) {
    ${(props) =>
      props.hasNumberedEntries &&
      `
      border-left: none;
    `}
  }

  > thead > tr > th:nth-child(${({ canEditContent }) => (canEditContent ? 2 : 1)}) {
    border-start-start-radius: ${tableBorderRadius};
  }

  > thead > tr > th.${TABLE_CLASSNAMES.ADD_FIELD} {
    z-index: 1;
    right: -2px;

    ${AddFieldButtonContainer} {
      position: absolute;
      right: 2px;
      top: 1px;
      height: calc(100% - 2px);
      width: 38px;
      border-width: ${newTableBorderWidth}px;
      border-top-right-radius: ${tableBorderRadius};
      border-left: 1px solid ${token('color.border')};
    }
  }

  > thead > tr > th:not(.${TABLE_CLASSNAMES.ADD_FIELD}) {
    ${FieldNameInlineContainer} {
      padding: 4px;
    }

    svg {
      height: 18px;
      width: auto;
    }
  }

  > thead > tr > th,
  > tfoot > tr > td,
  > tbody > tr > td {
    min-height: ${tableRowMinHeight}px;
  }

  > tbody > tr > *:nth-last-child(2) {
    border-right: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
  }

  > tbody > tr > :nth-child(3) {
    ${(props) =>
      props.hasNumberedEntries &&
      `
      border-left: none;
    `}
  }

  > tbody > tr:first-of-type > * {
    ${BorderContainer} {
      top: 1px;
    }
  }

  > tbody > tr:last-of-type > * {
    ${BorderContainer} {
      bottom: 1px;
    }
  }

  td {
    &.${TABLE_CLASSNAMES.IS_SELECTED} {
      &:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
        z-index: 1;
      }
    }

    &.${TABLE_CLASSNAMES.IS_SELECTED}:not(:focus-within) {
      background: ${token('color.background.accent.blue.subtlest')};
    }

    &.${TABLE_CLASSNAMES.IS_SOFT_SELECTED}:not(:focus-within) {
      background: transparent !important;
    }

    &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}:not(:focus-within) {
      opacity: 0.4;
    }
  }

  th.${TABLE_CLASSNAMES.ADD_FIELD}, td.${TABLE_CLASSNAMES.ACTIONS} {
    position: sticky;
    right: 0;
    background: ${token('elevation.surface')};
    border-left: ${subtleBorder};
    border-bottom: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
    min-width: ${(props) => `${props.actionsMidWidth}px`};
    justify-content: center;

    > * {
      flex-grow: 1;
    }
  }

  // Show insert dots of the next selected siblings when hovering a selected cell (only on column selection).

  > * > tr:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > *.${TABLE_CLASSNAMES.IS_SELECTED}:is(:hover) ~ *.${TABLE_CLASSNAMES.IS_SELECTED} ${StyledDot},

    // Show insert dots of the previous selected siblings when hovering a selected cell (only on column selection).
  > * > tr:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > *.${TABLE_CLASSNAMES.IS_SELECTED}:has(~ .${TABLE_CLASSNAMES.IS_SELECTED}:is(:hover)) ${StyledDot},

    // Show insert dots when hovering a single cell.
  > * > tr > td:hover ${StyledDot} {
    opacity: 1;
    z-index: ${Z_INDEX.TABLE_VIEW_INSERT_DOT};
  }
`;

export const TableLayoutWrapper = styled.div<{ containerHeight?: number }>`
  display: inline-block;
  margin-left: 10px;
  margin-right: 8px; // needed for the most right insert dot
  min-width: ${(props) =>
    `${
      props.containerWidth - 10 - 8
    }px`}; // if removing the 10px or 8px margin, it will need to be adjusted in QuickCalculationsRow.tsx
`;

export interface TableLayoutProps {
  editingIds: React.MutableRefObject<Set<string>>;
  contentId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  couldEditFields: boolean;
  renderSection: ScrollPosition;
  containerWidth: number;
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  containerTop: number;
  containerLeft: number;
  handleEntry: (entry: DatabaseEntry) => void;
}

const determineNewFieldWidth = (w: number | undefined) =>
  w != null ? Math.max(w!, TABLE_VIEW_HARD_COLUMN_MIN_WIDTH) : w;

const addEntryAndQuickCalculationsContainerStyles = xcss({
  position: 'sticky',
  bottom: 'space.0',
  backgroundColor: 'elevation.surface',
  zIndex: 'card',
});

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const TableLayout: FunctionComponent<TableLayoutProps> = ({
  editingIds,
  contentId,
  isContentEditable,
  canEditContent,
  canEditFields,
  couldEditFields,
  renderSection,
  containerWidth,
  addEntryButtonRef,
  containerTop,
  containerLeft,
  handleEntry,
}) => {
  const scope = useScope();
  const isQuickCalcEnabled = useBooleanFeatureFlag('databases-frontend-quick-calc');

  const { view } = useView();

  const [databaseDraftView, databaseDraftFieldWidths] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraftView = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        viewSettings: view.settings,
        includeDeleted: true,
      });
      return [
        databaseDraftView,
        getDatabaseFields(databaseDraftView).map((field) => field.width),
      ] as const;
    });

  const hasNumberedEntries = useHasNumberedEntries({ contentId, scope });

  // Hold the field widths in a local state while resizing the columns.
  const [resizingFieldWidths, setResizingFieldWidths] = useState<
    (number | undefined)[] | undefined
  >(undefined);
  const handleResize = useFlaggingCallback(
    (widths: Array<number | undefined>) =>
      setResizingFieldWidths(widths.map((w) => determineNewFieldWidth(w))),
    []
  );

  // Returns either the resizing field widths while resizing or the field widths from the store.
  const fieldWidths = useMemo(
    () => [
      hasNumberedEntries ? NUMBERED_COLUMN_WIDTH : 0,
      ...(resizingFieldWidths?.length ? resizingFieldWidths : databaseDraftFieldWidths),
    ],
    [hasNumberedEntries, resizingFieldWidths, databaseDraftFieldWidths]
  );

  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const handleSubmitResize = useFlaggingCallback(
    (newWidths: Array<number | undefined>) => {
      setResizingFieldWidths(undefined);
      batch(() => {
        collabDatabaseBatch(store, contentId, () => {
          for (let i = 0; i < newWidths.length; i++) {
            if (
              newWidths[i] !== databaseDraftFieldWidths[i] &&
              databaseDraftView.fields[i]
            ) {
              dispatch(
                addChangeToDraft(contentId, scope, {
                  type: DatabaseChangeType.SET_FIELD_WIDTH,
                  fieldId: databaseDraftView.fields[i],
                  width: determineNewFieldWidth(newWidths[i]),
                })
              );
            }
          }
        });
      });
    },
    [
      store,
      contentId,
      databaseDraftFieldWidths,
      databaseDraftView.fields,
      dispatch,
      scope,
    ]
  );

  const minWidth =
    fieldWidths.reduce<number>((p, w) => p + (w ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH), 0) +
    ACTIONS_MID_WIDTH;

  const gridTemplateColumns = useMemo(
    () =>
      `${
        canEditContent
          ? fieldWidths.some((w) => w == null)
            ? `${HALF_ROW_SELECT_BUTTON_WIDTH}px`
            : '0fr'
          : ''
      } ${getGridTemplateColumns(fieldWidths)} ${ACTIONS_MID_WIDTH}px`,
    [canEditContent, fieldWidths]
  );

  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const containerHeight =
    (tableRef.current?.offsetHeight ?? 0) + editingIds.current.size * tableRowMinHeight;

  const handleBlur = useCallback(() => {
    if (!(document.activeElement instanceof HTMLIFrameElement)) {
      dispatch(deselectAll());
    }
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleBlur]);

  useClearEntriesKeyboardShortcut({ contentId });

  const tableWidth = fieldWidths.reduce((accumulator, currentValue) => {
    if (accumulator !== undefined && currentValue !== undefined) {
      return accumulator + currentValue;
    }
  }, 0);

  return (
    <TableLayoutWrapper containerWidth={containerWidth}>
      <TableResizer
        widths={fieldWidths}
        onResizeSubmit={handleSubmitResize}
        onResize={handleResize}
        minWidth={TABLE_VIEW_HARD_COLUMN_MIN_WIDTH}
        isDisabled={!canEditFields}
        contentId={contentId}
        hasNumberedEntries={hasNumberedEntries}
      >
        {(currentWidths) => (
          <Table
            ref={tableRef}
            id="table-layout-table"
            minWidth={minWidth}
            gridTemplateColumns={gridTemplateColumns}
            actionsMidWidth={ACTIONS_MID_WIDTH}
            containerHeight={containerHeight}
            canEditContent={canEditContent}
            hasNumberedEntries={hasNumberedEntries}
          >
            <thead>
              <TableLayoutHeaderRow
                contentId={contentId}
                canEditContent={canEditContent}
                canEditFields={canEditFields}
                couldEditFields={couldEditFields}
                containerHeight={containerHeight}
                containerLeft={containerLeft}
                ref={tableBodyRef}
              />
            </thead>
            <TableLayoutRows
              addEntryButtonRef={addEntryButtonRef}
              contentId={contentId}
              isContentEditable={isContentEditable}
              canEditFields={canEditFields}
              canEditContent={canEditContent}
              editingIds={editingIds}
              renderSection={renderSection}
              currentWidths={currentWidths}
              containerWidth={containerWidth}
              containerTop={containerTop}
              ref={tableBodyRef}
            />
          </Table>
        )}
      </TableResizer>
      <Box xcss={addEntryAndQuickCalculationsContainerStyles}>
        {canEditContent && (
          <AddEntryButton
            addEntryButtonRef={addEntryButtonRef}
            contentId={contentId}
            onAdd={handleEntry}
            width={tableWidth}
          />
        )}
        {isQuickCalcEnabled && (
          <QuickCalculationsRow
            database={databaseDraftView}
            columnWidths={databaseDraftFieldWidths}
            containerWidth={containerWidth}
            canEditContent={canEditContent}
          ></QuickCalculationsRow>
        )}
      </Box>
    </TableLayoutWrapper>
  );
};

export default React.memo(TableLayout);
